import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import {
  notifyMilestoneCompleted,
  notifyPaymentReceived,
  notifyContractSigned,
  notifyContractFullySigned,
  notifyProjectCompleted,
} from "@/lib/emailService";

/**
 * Hook to handle real-time email notifications for important events
 * Listens to database changes and triggers appropriate emails
 */
export const useEmailNotifications = () => {
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;

    // Subscribe to milestone completions
    const milestonesChannel = supabase
      .channel("milestone-notifications")
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "milestones",
        },
        async (payload) => {
          // Only trigger on status change to completed
          if (payload.new.status === "completed" && payload.old?.status !== "completed") {
            console.log("Milestone completed:", payload.new.title);
            
            try {
              // Get project details to find the client
              const { data: project } = await supabase
                .from("projects")
                .select("id, title, conversation:conversations(client_id)")
                .eq("id", payload.new.project_id)
                .single();

              if (project?.conversation?.client_id) {
                await notifyMilestoneCompleted(
                  project.conversation.client_id,
                  payload.new.title,
                  project.title,
                  project.id
                );
              }
            } catch (error) {
              console.error("Failed to send milestone email:", error);
            }
          }
        }
      )
      .subscribe();

    // Subscribe to project completion (both parties approved)
    const projectsChannel = supabase
      .channel("project-completion-notifications")
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "projects",
        },
        async (payload) => {
          // Check if project just became completed
          if (payload.new.status === "completed" && payload.old?.status !== "completed") {
            console.log("Project completed:", payload.new.title);
            
            try {
              const { data: project } = await supabase
                .from("projects")
                .select("id, title, conversation:conversations(client_id, editor_id)")
                .eq("id", payload.new.id)
                .single();

              if (project?.conversation) {
                // Notify both client and editor
                await Promise.all([
                  notifyProjectCompleted(project.conversation.client_id, project.title, project.id),
                  notifyProjectCompleted(project.conversation.editor_id, project.title, project.id),
                ]);
              }
            } catch (error) {
              console.error("Failed to send project completion email:", error);
            }
          }
        }
      )
      .subscribe();

    // Subscribe to contract updates
    const contractsChannel = supabase
      .channel("contract-notifications")
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "contracts",
        },
        async (payload) => {
          try {
            const contract = payload.new;
            const oldContract = payload.old;

            // Check if client just signed
            if (contract.client_signed && !oldContract?.client_signed) {
              const { data: signerProfile } = await supabase
                .from("safe_profiles")
                .select("full_name")
                .eq("id", contract.client_id)
                .single();

              // Notify editor that client signed
              await notifyContractSigned(
                contract.editor_id,
                contract.title,
                signerProfile?.full_name || "Client",
                contract.id
              );
            }

            // Check if editor just signed
            if (contract.editor_signed && !oldContract?.editor_signed) {
              const { data: signerProfile } = await supabase
                .from("safe_profiles")
                .select("full_name")
                .eq("id", contract.editor_id)
                .single();

              // Notify client that editor signed
              await notifyContractSigned(
                contract.client_id,
                contract.title,
                signerProfile?.full_name || "Editor",
                contract.id
              );
            }

            // Check if contract is now fully signed
            if (
              contract.client_signed &&
              contract.editor_signed &&
              (!oldContract?.client_signed || !oldContract?.editor_signed)
            ) {
              // Notify both parties
              await Promise.all([
                notifyContractFullySigned(contract.client_id, contract.title, contract.id),
                notifyContractFullySigned(contract.editor_id, contract.title, contract.id),
              ]);
            }
          } catch (error) {
            console.error("Failed to send contract email:", error);
          }
        }
      )
      .subscribe();

    // Cleanup subscriptions
    return () => {
      supabase.removeChannel(milestonesChannel);
      supabase.removeChannel(projectsChannel);
      supabase.removeChannel(contractsChannel);
    };
  }, [user]);
};
